// Today view: one lane per prod desk, one bar per login that runs today (moved out of
// BrowserDeskConsole.js unchanged, except test runs no longer place a login's bar).

import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { BAD, daysText, deskLabel, deskPod, liveRunsToday, loginDesk, needsEnroll, pendingAt, scheduleOf, weekdayOf } from './deskModel';
import { Dot, MONO, deskColour, useTones } from './tones';

const fmtClock = (iso) => {
    try {
        return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } catch {
        return iso;
    }
};
const sameDay = (iso, nowMs) => new Date(iso).toDateString() === new Date(nowMs).toDateString();
const fmtDayClock = (iso) => {
    try {
        return new Date(iso).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' });
    } catch {
        return iso;
    }
};

const minuteOfDay = (iso) => { const d = new Date(iso); return d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60; };
const clockOfMinute = (m) => {
    const h = Math.floor(m / 60) % 24;
    return `${((h + 11) % 12) + 1}:${String(Math.round(m % 60)).padStart(2, '0')}`;
};
const BAR_STYLES = {
    done: { bgcolor: '#e6f4ea', color: '#1e5631', border: `1px solid #8cc79c` },
    late: { bgcolor: '#f3f3f3', color: '#444', border: '1px dashed #8a8a8a' },
    blocked: { background: 'repeating-linear-gradient(135deg, #fdf0e4 0 6px, #fbe2c8 6px 12px)', color: '#8a4b10', border: '1px dashed #d98a3d' },
};
const BAR_STYLES_DARK = {
    done: { bgcolor: alpha('#66bb6a', 0.2), color: '#a5d6a7', border: `1px solid ${alpha('#66bb6a', 0.6)}` },
    late: { bgcolor: 'rgba(255,255,255,0.06)', color: '#cbd5e1', border: '1px dashed #64748b' },
    blocked: {
        background: `repeating-linear-gradient(135deg, ${alpha('#f0a35e', 0.14)} 0 6px, ${alpha('#f0a35e', 0.26)} 6px 12px)`,
        color: '#ffcc80', border: '1px dashed #d98a3d',
    },
};
const barStyle = (t, kind, n) => {
    if (kind !== 'planned') return (t.dark ? BAR_STYLES_DARK : BAR_STYLES)[kind];
    const c = t.desk(n);
    return { bgcolor: c.bg, color: c.fg, border: `1px solid ${c.border}` };
};

// Where a login sits on today's axis, or why it isn't there. Bars mark when a run started
// (done) or is planned; the desk records no durations, so every bar is the same width.
const todayBar = (row, desks, nowMs) => {
    const runs = liveRunsToday(row);
    const last = runs[runs.length - 1];
    if (row.disabled) return { not: 'turned off' };
    if (row.fixing) return { not: 'being fixed on dev' };
    if (!row.enabled) return { not: 'not scheduled' };
    const rec = Object.values(desks || {}).find((d) => d && !d.stale && d.credential === row.id);
    // A running bar starts at the run's start if that was today, else now (never the next planned time).
    if (rec) {
        const started = rec.started_at && sameDay(rec.started_at, nowMs) ? rec.started_at : new Date(nowMs).toISOString();
        return { kind: 'planned', running: true, at: started, text: 'running now' };
    }
    if (row.today?.done && last) return { kind: 'done', at: last.at, text: `${fmtClock(last.at)} done` };
    if (needsEnroll(row)) {
        return row.today?.start ? { kind: 'blocked', at: row.today.start, text: 'not signed in' } : { not: 'not signed in' };
    }
    const at = pendingAt(row);
    if (at) {
        const late = new Date(at).getTime() < nowMs;
        return { kind: late ? 'late' : 'planned', at, text: late ? `planned ${fmtClock(at)}, late` : fmtClock(at) };
    }
    if (last) return { kind: 'late', at: last.at, text: `${fmtClock(last.at)} ${last.status}` };
    const sched = scheduleOf(row);
    if (row.today?.day && sched.days.length && !sched.days.includes(weekdayOf(row.today.day))) {
        return { not: `runs ${daysText(sched.days)}` };
    }
    return { not: 'no plan yet' };
};

const LANE_LABEL = 170;
const ROW_H = 26;

const TodayTimeline = ({ desks, prodDesks, logins }) => {
    const t = useTones();
    const nowMs = Date.now();
    const nowMin = minuteOfDay(new Date(nowMs).toISOString());
    // A time on another day (a plan for tomorrow, a stale one from yesterday) isn't on today's
    // axis: it goes in the Not today list, so it can't stretch the axis to midnight.
    const placed = logins.map((r) => {
        const p = { row: r, ...todayBar(r, desks, nowMs) };
        if (!p.at || sameDay(p.at, nowMs)) return p;
        const later = new Date(p.at).getTime() > nowMs;
        return { row: r, not: `${later ? 'next run' : p.kind === 'done' ? 'last run' : 'planned'} ${fmtDayClock(p.at)}` };
    });
    const bars = placed.filter((p) => p.at).map((p) => ({ ...p, min: minuteOfDay(p.at) }));
    const notToday = placed.filter((p) => p.not);

    // The axis covers today's runs, padded to half hours (room for the last bar); at least two hours wide.
    // A running bar counts at now: it is drawn so it reaches the now line.
    const mins = bars.map((b) => (b.running ? nowMin : b.min));
    let start = mins.length ? Math.floor((Math.min(...mins) - 15) / 30) * 30 : Math.floor(nowMin / 60) * 60 - 60;
    let end = mins.length ? Math.ceil((Math.max(...mins) + 40) / 30) * 30 : start + 180;
    if (end - start < 120) end = start + 120;
    start = Math.max(0, start); end = Math.min(24 * 60, end);
    const span = end - start;
    const barMin = span * 0.16; // uniform bar width, in axis minutes
    for (const b of bars) if (b.running) b.min = Math.max(b.min, nowMin - barMin * 0.8, start);
    const pct = (m) => `${((m - start) / span) * 100}%`;
    const step = span <= 240 ? 30 : 60;
    const ticks = [];
    for (let m = start; m <= end; m += step) ticks.push(m);
    const nowInRange = nowMin >= start && nowMin <= end;

    const lanes = prodDesks.map((d) => {
        // Stack bars that would overlap onto the next row of the lane.
        const rowsEnd = [];
        const laneBars = bars.filter((b) => loginDesk(b.row) === d.prod_desk).sort((a, b) => a.min - b.min).map((b) => {
            let i = rowsEnd.findIndex((e) => e <= b.min);
            if (i < 0) { i = rowsEnd.length; rowsEnd.push(0); }
            rowsEnd[i] = b.min + barMin + span * 0.005;
            return { ...b, lane: i };
        });
        return { desk: d, bars: laneBars, rows: Math.max(1, rowsEnd.length) };
    });

    const legend = [
        ['done', <Box component="span" sx={{ width: 14, height: 10, borderRadius: '3px', ...barStyle(t, 'done') }} />],
        ['planned', <Box component="span" sx={{ width: 14, height: 10, borderRadius: '3px', ...barStyle(t, 'planned', 0) }} />],
        ['late (planned time passed, not done)', <Box component="span" sx={{ width: 14, height: 10, borderRadius: '3px', ...barStyle(t, 'late') }} />],
        ['not signed in on its desk (waiting for Enroll now)', <Box component="span" sx={{ width: 14, height: 10, borderRadius: '3px', ...barStyle(t, 'blocked') }} />],
        [`now ${fmtClock(new Date(nowMs).toISOString())}`, <Box component="span" sx={{ width: 2, height: 12, bgcolor: BAD }} />],
    ];

    return (
        <Box>
            <Typography className="d-font" sx={{ fontSize: 12, color: t.muted, mb: 1 }}>
                Today in your time zone. Each lane is one prod desk (one browser); runs on different desks can overlap.
                Bars mark when a run started or is planned, not how long it took.
            </Typography>
            <Box sx={{ overflowX: 'auto', border: '1px solid var(--color-border, #e0e0e0)', borderRadius: 1, p: 2 }}>
                <Box sx={{ minWidth: 720, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: `${LANE_LABEL}px 1fr`, gap: 1.5 }}>
                        <Box />
                        <Box sx={{ position: 'relative', height: 18 }}>
                            {ticks.map((m) => (
                                <Typography key={m} component="span" sx={{
                                    position: 'absolute', left: pct(m), fontFamily: MONO, fontSize: 11, color: t.muted,
                                    transform: m === start ? 'none' : m === end ? 'translateX(-100%)' : 'translateX(-50%)',
                                }}>{clockOfMinute(m)}</Typography>
                            ))}
                        </Box>
                    </Box>
                    {lanes.map(({ desk, bars: lb, rows }) => (
                        <Box key={desk.name} data-lane={desk.name} sx={{ display: 'grid', gridTemplateColumns: `${LANE_LABEL}px 1fr`, gap: 1.5, alignItems: 'center' }}>
                            <Box>
                                <Typography className="d-font" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, fontSize: 14 }}>
                                    <Dot colour={deskColour(desk.prod_desk).dot} />{deskLabel(desk.name)}
                                </Typography>
                                <Typography sx={{ fontFamily: MONO, fontSize: 11, color: t.muted }}>{desk.pod || deskPod(desk.name)}</Typography>
                            </Box>
                            <Box sx={{ position: 'relative', height: rows * ROW_H + 10, bgcolor: t.dark ? 'var(--color-bg-subtle)' : '#f5f6f8', borderRadius: 1.5 }}>
                                {ticks.map((m) => (
                                    <Box key={m} sx={{ position: 'absolute', top: 0, bottom: 0, left: pct(m), width: '1px', bgcolor: t.dark ? 'divider' : '#e6e8ec' }} />
                                ))}
                                {!lb.length && (
                                    <Typography className="d-font" sx={{ position: 'absolute', left: 10, top: 9, fontSize: 12, color: t.muted }}>
                                        No runs today
                                    </Typography>
                                )}
                                {lb.map((b) => (
                                    <Tooltip key={b.row.id} title={`${b.row.id} · ${b.text}`}>
                                        <Box className="d-font" data-bar={b.row.id} data-kind={b.kind} sx={{
                                            position: 'absolute', top: 5 + b.lane * ROW_H, left: pct(b.min), width: pct(start + barMin),
                                            height: ROW_H - 4, boxSizing: 'border-box', borderRadius: '5px', px: 0.75, fontSize: 11,
                                            lineHeight: `${ROW_H - 6}px`, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                                            ...barStyle(t, b.kind, desk.prod_desk),
                                        }}>
                                            <b>{b.row.id}</b> · {b.text}
                                        </Box>
                                    </Tooltip>
                                ))}
                                {nowInRange && (
                                    <Box aria-label="now" data-now sx={{ position: 'absolute', top: -4, bottom: -4, width: 2, bgcolor: BAD, left: pct(nowMin) }} />
                                )}
                            </Box>
                        </Box>
                    ))}
                    <Box sx={{ display: 'grid', gridTemplateColumns: `${LANE_LABEL}px 1fr`, gap: 1.5 }}>
                        <Box />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, fontSize: 12, color: t.soft }} className="d-font">
                            {legend.map(([label, swatch]) => (
                                <Box key={label} component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>{swatch}{label}</Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Box>
            {notToday.length > 0 && (
                <Typography className="d-font" sx={{ fontSize: 12, color: t.soft, mt: 1 }}>
                    Not today: {notToday.map((p) => `${p.row.id} (${p.not})`).join(', ')}
                </Typography>
            )}
        </Box>
    );
};

export default TodayTimeline;
